/**
 * Indirect Sales Dashboard
 *
 * Leads coming through brokers, channel partners, referral partners, or
 * agencies. The field model and pipeline come straight from the CRM spec:
 *
 *   Partner Referral → Verification → Internal Assignment → Client Contact
 *   → Site Visit → Deal Closure → Commission Approval → Partner Payment Done
 *
 * Data sources:
 * - Partners: existing `brokers` collection
 * - Sales: existing `broker-sales` collection
 *
 * Fields the spec asks for that aren't on the backend yet render as "—" so
 * the wiring lands cleanly when the data ships.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import HelpInfo from '../components/HelpInfo';
import { toast } from 'sonner';
import {
  Handshake, Users, Building2, FileCheck, DollarSign, Wallet, ChevronRight,
  Eye, Filter, RefreshCw, Search, AlertTriangle, CheckCircle2,
  Columns3, Check, ArrowUpRight, FileText, CalendarDays, X,
} from 'lucide-react';
import LeadDocumentsPanel from './BrokerageHub/components/LeadDocumentsPanel';
import { fetchIndirectSalesMetrics } from './BrokerageHub/api/brokerages';
import InfoBadge from '../components/InfoBadge';

// Backend mounts these routers under /api — without the prefix the
// fetches hit the wrong path and 404, which is why both tables were empty.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pipeline stages for the Indirect Sales / broker-sales flow.
//
// Same two-layer matcher as Direct Sales: `exact` is the canonical list
// matched case-insensitively first, `contains` is a substring fallback
// (with longest-match priority) for less-standard backend variants.
const PIPELINE_STAGES = [
  { key: 'referral',       label: 'Partner Referral',     color: 'bg-slate-500',
    exact: ['referral', 'new', 'received', 'pending'], contains: ['referral', 'received'] },
  { key: 'verification',   label: 'Verification',         color: 'bg-blue-500',
    exact: ['verification', 'pending_verification', 'verifying'], contains: ['verifi'] },
  { key: 'assignment',     label: 'Internal Assignment',  color: 'bg-cyan-500',
    exact: ['assigned', 'assignment'], contains: ['assign'] },
  // MoM 05 Jun 2026 §7 — finance calls this stage "Prospecting".
  // We keep the canonical key `client_contact` (used by the funnel
  // counts and analytics joins) but change the visible label.
  { key: 'client_contact', label: 'Prospecting',           color: 'bg-violet-500',
    exact: ['contacted', 'client_contact', 'in_progress', 'prospecting'], contains: ['contact', 'prospect'] },
  { key: 'site_visit',     label: 'Site Visit',           color: 'bg-amber-500',
    exact: ['site_visit', 'visit', 'visited', 'viewing'], contains: ['visit', 'view'] },
  { key: 'closure',        label: 'Deal Closure',         color: 'bg-orange-500',
    exact: ['negotiation', 'booking', 'booking_initiated', 'booked',
            'closed_won', 'won', 'closed', 'converted'],
    contains: ['negotiation', 'booking', 'won', 'closed', 'converted'] },
  { key: 'commission',     label: 'Commission Approval',  color: 'bg-emerald-500',
    exact: ['commission_pending', 'commission_approved', 'approval', 'commission'],
    contains: ['commission', 'approval'] },
  { key: 'paid',           label: 'Partner Paid',         color: 'bg-green-600',
    exact: ['paid', 'payment_done', 'completed'], contains: ['paid', 'payment_done', 'completed'] },
];

const STATUS_TO_STAGE = (() => {
  const m = {};
  for (const st of PIPELINE_STAGES) {
    for (const e of (st.exact || [])) m[e.toLowerCase()] = st.key;
  }
  return m;
})();

// Matchers for the KPI drill-down tiles on the Broker Hub lifecycle
// strip. Each matcher takes a lead row and returns true if that row
// is "behind" the tile's number — i.e. clicking the tile should bring
// up exactly the rows where this returns true.
//
// Keep these aligned with the equivalent rules in the Python
// /indirect-sales/metrics endpoint so the tile count == filtered table
// length. If a definition changes there, mirror it here.
const HUB_FUNNEL_FE = [
  'new', 'contacted', 'viewing', 'negotiation', 'booking_initiated', 'won',
];
const WON_SET = new Set([
  'won', 'closed_won', 'closed', 'converted', 'paid', 'completed', 'booked',
]);
const LOST_SET = new Set(['lost', 'closed_lost', 'rejected', 'cancelled', 'dropped']);
const BOOKING_SET = new Set(['booking_initiated', 'booked', ...WON_SET]);

const KPI_MATCHERS = {
  total: () => true,
  active: (r) => {
    const s = (r.status || 'new').toLowerCase();
    return !(WON_SET.has(s) || LOST_SET.has(s));
  },
  visits_scheduled: (r) => Boolean(r.site_visit_date),
  visits_completed: (r) => {
    const s = (r.status || '').toLowerCase();
    const idx = HUB_FUNNEL_FE.indexOf(s);
    const viewingIdx = HUB_FUNNEL_FE.indexOf('viewing');
    if (idx >= 0 && viewingIdx >= 0 && idx >= viewingIdx) return true;
    return Boolean((r.site_visit_outcome || '').trim());
  },
  bookings: (r) => {
    const s = (r.status || '').toLowerCase();
    return BOOKING_SET.has(s) || Boolean(r.booking_date);
  },
  // Conversion-rate tile drills into the same set as bookings — the
  // rate IS bookings/total, so showing those rows is the most useful
  // breakdown the user could click into.
  conversion: (r) => {
    const s = (r.status || '').toLowerCase();
    return BOOKING_SET.has(s) || Boolean(r.booking_date);
  },
};

const KPI_LABELS = {
  total: 'Total leads from brokers',
  active: 'Active leads',
  visits_scheduled: 'Visits scheduled',
  visits_completed: 'Visits completed',
  bookings: 'Bookings generated',
  conversion: 'Converted (bookings)',
};

// Same business rule as Direct Sales: a partner-referred lead with no
// internal agent assigned can only be in "Partner Referral" — it hasn't
// entered the working pipeline. Internal assignment is the prerequisite
// for any post-referral stage.
const stageForSale = (row) => {
  const agent = (row?.assigned_agent_name || row?.assigned_to_name
    || row?.owner_name || row?.assigned_to || row?.owner_id || '').toString().trim();
  const s = (row?.status || '').toLowerCase().trim();
  if (!agent) return 'referral';
  if (!s || ['referral', 'new', 'received', 'pending'].includes(s)) return 'assignment';
  if (STATUS_TO_STAGE[s]) return STATUS_TO_STAGE[s];
  let bestStage = 'assignment';
  let bestLen = -1;
  for (const st of PIPELINE_STAGES) {
    for (const m of (st.contains || [])) {
      if (s.includes(m) && m.length > bestLen) { bestStage = st.key; bestLen = m.length; }
    }
  }
  return bestStage;
};

// Back-compat wrapper for callers that pass just a status string.
const stageFor = (statusOrRow) => {
  if (statusOrRow && typeof statusOrRow === 'object') return stageForSale(statusOrRow);
  return stageForSale({ assigned_agent_name: '_assumed', status: statusOrRow });
};

const PAYMENT_BADGE = {
  pending:  'bg-amber-100 text-amber-700 border-amber-300',
  approved: 'bg-blue-100 text-blue-700 border-blue-300',
  paid:     'bg-emerald-100 text-emerald-700 border-emerald-300',
  failed:   'bg-red-100 text-red-700 border-red-300',
  rejected: 'bg-red-100 text-red-700 border-red-300',
};

const fieldValue = (v) => (v === null || v === undefined || v === '') ? '—' : v;

/**
 * Render a client-budget cell. Supports three shapes that the lead form
 * accepts:
 *   - both blank                → '—'
 *   - min only (single figure)  → 'AED 1.5M'
 *   - both filled (range)       → 'AED 1.5M – 2M'
 * Numbers ≥ 1,000,000 collapse to 'XM' / 'X.XM' for table density; below
 * that we use the user's locale formatting so 250,000 reads as '250,000'
 * not '250K' (which would be misleading at small amounts).
 */
const formatBudget = (min, max, currency = 'AED') => {
  const fmt = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v) || v <= 0) return null;
    if (v >= 1_000_000) {
      const m = v / 1_000_000;
      // Trim trailing .0 so '1.0M' renders as '1M'.
      return `${m.toFixed(m >= 10 ? 0 : 1).replace(/\.0$/, '')}M`;
    }
    if (v >= 100_000) {
      return `${(v / 1000).toFixed(0)}K`;
    }
    return v.toLocaleString();
  };
  const a = fmt(min);
  const b = fmt(max);
  if (!a && !b) return '—';
  const cur = currency || 'AED';
  if (a && b && a !== b) return `${cur} ${a} – ${b}`;
  return `${cur} ${a || b}`;
};

// ============ Sectioned field list ============
const FieldSection = ({ title, icon: Icon, fields }) => (
  <div>
    <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-slate-700 dark:text-slate-200">
      {Icon && <Icon size={14} />}
      {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      {fields.map(([label, value, opts]) => {
        // `opts.fullWidth` lets a field span both columns of the grid —
        // used for long-form text like the Notes / Main Overview where
        // the truncated right-aligned default would be unreadable.
        if (opts?.fullWidth) {
          return (
            <div key={label} className="sm:col-span-2 py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
              <div className="text-slate-500 dark:text-slate-400 text-xs uppercase mb-0.5">{label}</div>
              <div className="text-slate-900 dark:text-slate-100 whitespace-pre-wrap break-words">
                {fieldValue(value)}
              </div>
            </div>
          );
        }
        return (
          <div key={label} className="flex justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="font-medium text-slate-900 dark:text-slate-100 text-right truncate" title={String(value ?? '')}>
              {fieldValue(value)}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

// Render a friendly "Booking Status" string from whatever stage the
// lead is in. We don't store this as a separate column — it's derived
// from the canonical status field so it can't drift.
const deriveBookingStatus = (row) => {
  const s = (row?.status || '').toLowerCase();
  if (s === 'booking_initiated') return 'Booking Initiated';
  if (s === 'won' || s === 'closed_won' || s === 'booked' || s === 'converted') return 'Booked';
  if (s === 'cancelled') return 'Cancelled';
  if (s === 'lost') return 'Lost';
  if (row?.booking_date) return 'Booking in progress';
  return null;
};

// ============ Detail dialog ============
const SaleDetailDialog = ({ row, partner, open, onOpenChange }) => {
  if (!row) return null;
  const fmt = (d) => d ? new Date(d).toLocaleString() : '';
  const sections = [
    { title: 'Partner Information', icon: Handshake, fields: [
      ['Partner ID', partner?.id?.slice(0, 8) || row.broker_id?.slice(0, 8)],
      ['Partner Company Name', partner?.company_name || partner?.name || row.broker_name],
      ['Partner Type', partner?.partner_type || partner?.type || 'Broker'],
      ['Partner Contact Person', partner?.contact_person],
      ['Partner Mobile Number', partner?.mobile || partner?.phone],
      ['Partner Email', partner?.email],
      ['RERA / License Number', partner?.rera_number || partner?.license_number],
      ['VAT Number', partner?.vat_number],
      ['Commission Agreement', partner?.commission_agreement],
      ['Partner Status', partner?.status],
    ] },
    { title: 'Lead Information', icon: Users, fields: [
      ['Lead ID', row.id?.slice(0, 8)],
      // Source defaults to "Broker Hub" when a broker is attached;
      // direct leads fall back to whatever the legacy `lead_source`
      // string says (typically empty for older rows).
      ['Lead Source', row.broker_id ? 'Broker Hub' : (row.lead_source || '—')],
      ['Lead Created Date', fmt(row.created_at)],
      ['Client Name', row.client_name],
      ['Client Mobile', row.client_mobile || row.client_phone],
      ['Client Email', row.client_email],
      ['Nationality', row.customer_nationality],
      ['Preferred Contact', row.preferred_contact_method],
      // Show the new structured budget if present, otherwise fall back
      // to the legacy free-text `budget` field so old leads still
      // render something sensible.
      ['Client Budget',
        (row.client_budget_min || row.client_budget_max)
          ? formatBudget(row.client_budget_min, row.client_budget_max, row.client_budget_currency)
          : row.budget],
      // Multi-project: join the names array if present; otherwise
      // fall back to the legacy single-string fields.
      ['Project Enquired',
        (Array.isArray(row.project_enquired_names) && row.project_enquired_names.length)
          ? row.project_enquired_names.join(', ')
          : (row.project_name || row.interested_project)],
      ['Property Type', row.property_type],
      ['Unit Type', row.unit_type],
      ['Preferred Unit #', row.preferred_unit_number],
      ['Preferred Location', row.preferred_location],
      ['Lead Status', row.status],
      // The spec's "Notes / Main Overview" — rendered full-width
      // below the other field grid via a tiny convention: a `[label,
      // value, {fullWidth: true}]` tuple. See the renderer below for
      // the wide-row branch.
      ['Notes / Main Overview', row.lead_overview, { fullWidth: true }],
    ] },
    { title: 'Referral & Attribution', icon: ChevronRight, fields: [
      ['Referred By', row.referred_by || partner?.name],
      ['Referral Date', fmt(row.referral_date || row.created_at)],
      ['Referral Source', row.referral_source],
      ['Referral Campaign', row.referral_campaign],
      ['Sub Broker Name', row.sub_broker_name],
      ['Parent Partner', row.parent_partner],
    ] },
    { title: 'Assignment & Tracking', icon: Building2, fields: [
      ['Assigned Indirect Sales Executive', row.assigned_to_name || row.assigned_agent_name],
      ['Team / Branch', row.team_branch],
      ['Manager', row.manager_name],
      ['Assigned Date', fmt(row.assigned_at)],
      ['First Response Time', row.first_response_time_minutes ? `${row.first_response_time_minutes} min` : null],
      ['First Contact Date', fmt(row.first_contact_at)],
      ['Last Follow-up', fmt(row.last_follow_up_at)],
      ['Next Follow-up', fmt(row.next_follow_up_at)],
      ['Meeting Details', row.meeting_details, { fullWidth: true }],
      ['Follow-Up Status', row.follow_up_status],
      ['Site Visit Status', row.site_visit_status],
      ['Meeting Status', row.meeting_status],
    ] },
    // Structured site-visit capture — populated by the Brokerage Hub
    // when a lead moves to `viewing` or when the broker explicitly
    // fills these in. Read-only from the Indirect Sales side; the
    // Brokerage Hub is the system of record.
    { title: 'Site Visit (UAE walkthrough)', icon: CalendarDays, fields: [
      ['Site Visit Date', fmt(row.site_visit_date)],
      ['Site Visit Agent', row.site_visit_agent],
      ['Outcome', row.site_visit_outcome],
      ['Visit Feedback', row.site_visit_feedback, { fullWidth: true }],
    ] },
    // Booking sub-record — only meaningful past booking_initiated, but
    // we always render the section (with em-dashes for empty fields)
    // so it's clear which slots will be filled when a deal advances.
    { title: 'Booking', icon: Building2, fields: [
      ['Booking Status', deriveBookingStatus(row)],
      ['Unit Selected', row.booked_unit_number],
      ['Booking Amount', row.booking_amount
        ? `AED ${Number(row.booking_amount).toLocaleString()}` : null],
      ['Booking Date', fmt(row.booking_date)],
      ['Payment Status', row.payment_status],
      ['Reservation Details', row.reservation_notes, { fullWidth: true }],
    ] },
    { title: 'Commission & Payout', icon: DollarSign, fields: [
      ['Commission Eligibility', row.commission_eligibility],
      ['Commission %', row.commission_percent ? `${row.commission_percent}%` : null],
      ['Fixed Commission', row.fixed_commission],
      ['Commission / Payout Amount', row.payout_amount || row.commission_amount],
      ['Commission Payment Status', row.commission_payment_status],
      ['Invoice Number', row.invoice_number],
      ['Invoice Date', fmt(row.invoice_date)],
      ['Payment Date', fmt(row.payment_date)],
      ['PO Number', row.po_number],
      ['Payment Request Status', row.payment_request_status],
    ] },
    { title: 'Compliance & Verification', icon: FileCheck, fields: [
      ['Partner Documents', partner?.documents_uploaded ? 'Uploaded' : 'Missing'],
      ['Trade License Copy', partner?.trade_license_url ? 'On file' : 'Missing'],
      ['Emirates ID / Passport', partner?.id_document_url ? 'On file' : 'Missing'],
      ['Bank Details', partner?.bank_details ? 'On file' : 'Missing'],
      ['Signed Agreement', partner?.agreement_signed ? 'Signed' : 'Not signed'],
      ['Verification Status', partner?.verification_status],
    ] },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake size={18} className="text-violet-500" />
            {row.client_name || 'Indirect Sale'}
            {partner?.partner_type && (
              <Badge variant="outline" className="text-xs">{partner.partner_type}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Full field set for this partner-referred lead — partner profile, attribution, commissions and compliance.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="space-y-5">
            {sections.map((s) => <FieldSection key={s.title} {...s} />)}

            {/* UAE documents — read-only here because the Brokerage Hub
                is the system of record for upload/delete. Same files
                the broker sees: both modules share the lead row. */}
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileText size={14} className="text-violet-500" />
                Documents (UAE)
                <span className="text-[11px] font-normal text-slate-400">
                  · managed in Brokerage Hub
                </span>
              </div>
              <LeadDocumentsPanel leadId={row.id} readOnly compact />
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

// ============ Main page ============
export default function IndirectSalesDashboard() {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [partnerTypeFilter, setPartnerTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  // Agent filter — the INTERNAL sales agent assigned to a partner-
  // referred lead (NOT the partner who referred it; partner is its own
  // dropdown via partnerTypeFilter and the partner column).
  const [agentFilter, setAgentFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Spec-driven KPI strip — one round-trip to /indirect-sales/metrics.
  // We keep it as its own state slice so the strip can render with a
  // skeleton without blocking the main lead table.
  const [metrics, setMetrics] = useState(null);
  // Active drill-down. When the user clicks a KPI tile we narrow the
  // main lead table to just the rows behind that number, the same way
  // the stage / payment / agent filters work — but driven by a
  // matcher function rather than an equality check.
  const [metricFilter, setMetricFilter] = useState(null);

  // Click handler for a KPI tile: toggle the drill-down (clicking the
  // same tile twice clears it). When we drill in, we reset the other
  // narrowing filters (stage / agent / partner type / payment / search)
  // because they would otherwise intersect with the KPI rule and the
  // visible row count would silently disagree with the tile number —
  // confusing and easy to mistake for a bug. The user can re-apply
  // those filters on top after the drill if they want a sub-slice.
  //
  // We also scroll the lead table into view so the result is obvious
  // on smaller screens while keeping the KPI strip on screen above.
  const drillInto = (key) => {
    setMetricFilter((curr) => {
      const next = curr === key ? null : key;
      if (next) {
        // Reset competing filters so the table matches the tile count.
        setStageFilter('all');
        setAgentFilter('all');
        setPartnerTypeFilter('all');
        setPaymentFilter('all');
        setSearch('');
      }
      return next;
    });
    requestAnimationFrame(() => {
      const el = document.getElementById('indirect-sales-leads-table');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  // Whenever the user touches one of the other filters (stage chip,
  // agent dropdown, etc.), drop the KPI drill — same reasoning in
  // reverse: the chip in the table header would then be inaccurate.
  // We do this with a tiny wrapper around the existing setters so the
  // change is purely additive and the rest of the page stays untouched.
  const clearDrillThen = (setter) => (val) => {
    if (metricFilter) setMetricFilter(null);
    setter(val);
  };

  // Column visibility for the lead table. The defaults match what
  // existed before the dedup spec — the new fields (budget, overview)
  // are hidden by default to keep the table from blowing out
  // horizontally, but the user can flip them on from the Columns
  // picker. Saved to localStorage so the choice persists per-user.
  const COLUMN_DEFS = useMemo(() => ([
    { key: 'client',     label: 'Client',     defaultOn: true,  required: true },
    { key: 'partner',    label: 'Partner',    defaultOn: true },
    { key: 'project',    label: 'Project',    defaultOn: true },
    { key: 'stage',      label: 'Stage',      defaultOn: true },
    { key: 'payout',     label: 'Payout',     defaultOn: true },
    { key: 'payment',    label: 'Payment',    defaultOn: true },
    { key: 'budget',     label: 'Client budget',  defaultOn: false },
    { key: 'overview',   label: 'Notes / overview', defaultOn: false },
    { key: 'view',       label: 'View',       defaultOn: true,  required: true },
  ]), []);
  const COLUMNS_LS_KEY = 'indirect_sales_dashboard_columns_v1';
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLUMNS_LS_KEY) || 'null');
      if (saved && typeof saved === 'object') return saved;
    } catch (_) { /* ignore */ }
    const init = {};
    COLUMN_DEFS.forEach((c) => { init[c.key] = c.defaultOn; });
    return init;
  });
  useEffect(() => {
    try { localStorage.setItem(COLUMNS_LS_KEY, JSON.stringify(visibleCols)); } catch (_) {}
  }, [visibleCols]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const showCol = (k) => visibleCols[k] !== false;

  const fetchAll = () => {
    if (!token) return;
    setLoading(true);
    // Progressive — partners and sales each populate as they arrive.
    // Surface real fetch errors as toasts (auth, 404, etc.) so the empty
    // state isn't ambiguous between "no data" and "couldn't load data".
    const explain = (label, e) => {
      console.error(`[IndirectSales] ${label} failed:`, e);
      const msg = e?.response?.data?.detail || e?.response?.data?.error || e.message;
      toast.error(`Couldn't load ${label}: ${msg}`);
    };
    const tasks = [
      axios.get(`${API}/brokers?token=${token}`)
        .then((r) => setPartners(r.data.brokers || []))
        .catch((e) => explain('partners', e)),
      axios.get(`${API}/broker-sales?token=${token}&limit=500`)
        .then((r) => setSales(r.data.sales || []))
        .catch((e) => explain('sales', e)),
      // KPI metrics — separate endpoint that does the aggregation
      // server-side so the strip doesn't have to wait for all 500
      // leads to download. Soft-fails: an empty strip is acceptable.
      fetchIndirectSalesMetrics(token)
        .then((m) => setMetrics(m))
        .catch((e) => console.warn('[IndirectSales] metrics failed', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [token]);

  // Index partners by id for O(1) lookup in the detail dialog.
  const partnerById = useMemo(() => {
    const m = {};
    partners.forEach((p) => { m[p.id] = p; });
    return m;
  }, [partners]);

  // Apply every non-stage filter so the pipeline chip counts reflect
  // the currently-selected agent / partner type / payment / search.
  // Without this, the counts were always global and the chips behaved
  // as a separate filter that ignored the dropdowns above.
  const nonStageFiltered = useMemo(() => sales.filter((row) => {
    if (paymentFilter !== 'all' && (row.payment_status || '').toLowerCase() !== paymentFilter) return false;
    if (partnerTypeFilter !== 'all') {
      const partner = partnerById[row.broker_id];
      const t = partner?.partner_type || partner?.type;
      if (t !== partnerTypeFilter) return false;
    }
    if (agentFilter !== 'all') {
      const agentName = row.assigned_agent_name || row.assigned_to_name || row.owner_name;
      if (agentFilter === '__unassigned__') {
        if (agentName) return false;
      } else if (agentName !== agentFilter) {
        return false;
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const partner = partnerById[row.broker_id];
      const hay = [
        row.client_name, row.client_email, row.client_mobile, row.project_name,
        row.assigned_agent_name, row.assigned_to_name, row.owner_name,
        partner?.company_name, partner?.name, partner?.contact_person, partner?.email,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [sales, partnerById, paymentFilter, partnerTypeFilter, agentFilter, search]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, 0]));
    nonStageFiltered.forEach((s) => { counts[stageFor(s)] = (counts[stageFor(s)] || 0) + 1; });
    return counts;
  }, [nonStageFiltered]);
  const nonStageTotal = nonStageFiltered.length;

  // Agent × Stage breakdown. Same shape as Direct Sales: built from the
  // non-agent-filtered set so the manager sees every internal agent who
  // owns at least one partner-referred lead.
  const agentMatrix = useMemo(() => {
    const baseSet = sales.filter((row) => {
      if (paymentFilter !== 'all' && (row.payment_status || '').toLowerCase() !== paymentFilter) return false;
      if (partnerTypeFilter !== 'all') {
        const partner = partnerById[row.broker_id];
        const t = partner?.partner_type || partner?.type;
        if (t !== partnerTypeFilter) return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const partner = partnerById[row.broker_id];
        const hay = [
          row.client_name, row.client_email, row.client_mobile, row.project_name,
          row.assigned_agent_name, row.assigned_to_name, row.owner_name,
          partner?.company_name, partner?.name, partner?.contact_person, partner?.email,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const rows = new Map();
    const ensure = (name) => {
      if (!rows.has(name)) {
        const init = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, 0]));
        rows.set(name, { ...init, total: 0 });
      }
      return rows.get(name);
    };
    for (const r of baseSet) {
      const name = r.assigned_agent_name || r.assigned_to_name || r.owner_name || '— Unassigned';
      const stage = stageFor(r.status);
      const row = ensure(name);
      row[stage] = (row[stage] || 0) + 1;
      row.total += 1;
    }
    return Array.from(rows.entries())
      .map(([name, counts]) => ({ name, ...counts }))
      .sort((a, b) => {
        if (a.name === '— Unassigned') return -1;
        if (b.name === '— Unassigned') return 1;
        if (b.total !== a.total) return b.total - a.total;
        return a.name.localeCompare(b.name);
      });
  }, [sales, partnerById, paymentFilter, partnerTypeFilter, search]);

  const partnerTypeOptions = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => {
      const t = p.partner_type || p.type;
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [partners]);

  // Internal agents who own at least one partner-referred lead. Built
  // from the data the table renders so the dropdown stays accurate.
  const agentOptions = useMemo(() => {
    const counts = new Map();
    let unassigned = 0;
    sales.forEach((s) => {
      const name = s.assigned_agent_name || s.assigned_to_name || s.owner_name;
      if (!name) { unassigned += 1; return; }
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const list = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { list, unassigned };
  }, [sales]);

  // Final filtered list = non-stage filtered set ∩ stage filter. Shared
  // source means chip counts and the table rows can't drift apart.
  const filtered = useMemo(() => {
    let rows = stageFilter === 'all'
      ? nonStageFiltered
      : nonStageFiltered.filter((row) => stageFor(row) === stageFilter);
    // KPI drill-down — applied AFTER stage filtering so the two layers
    // compose. The matcher table mirrors the same rules the backend
    // /indirect-sales/metrics endpoint uses, so the row count matches
    // the tile number.
    if (metricFilter && KPI_MATCHERS[metricFilter]) {
      rows = rows.filter(KPI_MATCHERS[metricFilter]);
    }
    return rows;
  }, [nonStageFiltered, stageFilter, metricFilter]);

  // Aggregate payout dollars for the summary tile.
  const totalPayout = useMemo(
    () => sales.reduce((acc, s) => acc + (Number(s.payout_amount || s.commission_amount) || 0), 0),
    [sales]
  );
  const paidPayout = useMemo(
    () => sales
      .filter((s) => (s.payment_status || '').toLowerCase() === 'paid')
      .reduce((acc, s) => acc + (Number(s.payout_amount || s.commission_amount) || 0), 0),
    [sales]
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Handshake className="text-violet-500" size={28} />
              Indirect Sales Dashboard
              <HelpInfo
                align="inline"
                title="How to use the Indirect Sales Dashboard"
                steps={[
                  <>The summary tiles cover your partner network — active partners, referred leads, and committed vs. paid payouts.</>,
                  <>The <strong>Pipeline</strong> strip runs from Partner Referral through to Partner Paid — click a stage chip to filter the leads table to that stage.</>,
                  <>A partner-referred lead with no internal agent assigned stays in <em>Partner Referral</em> — internal assignment is what moves it into the working pipeline.</>,
                  <>The <strong>Agent Performance</strong> matrix shows how referred leads are distributed across your internal agents; click an agent or stage cell to drill in.</>,
                  <>For the full partner, commission and compliance breakdown of any lead, click the eye icon on its row. To manage the brokerage firms themselves, use the <strong>Brokerage Hub</strong>.</>,
                ]}
                tip="Watch the gap between committed and paid payouts — a large gap means commissions are approved but not yet disbursed to partners."
              />
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Leads from brokers, channel partners and referrers — referral attribution, commissions and partner payouts.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary tiles — partner count, lead count, totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Handshake size={14} /> <span className="flex-1">Active Partners</span>
              <InfoBadge text="Brokers, referral partners, and agencies whose status is not Inactive. The denominator is every partner record ever created — including ones you've stopped working with." />
            </div>
            <div className="text-2xl font-bold mt-1">{partners.filter((p) => (p.status || '').toLowerCase() !== 'inactive').length}</div>
            <div className="text-xs text-slate-400">of {partners.length} total</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Users size={14} /> <span className="flex-1">Referred Leads</span>
              <InfoBadge text="All-time count of leads that came in through any partner / broker / agency. Direct leads (no broker_id) are NOT counted here — they live on the Direct Sales dashboard." />
            </div>
            <div className="text-2xl font-bold mt-1">{sales.length}</div>
            <div className="text-xs text-slate-400">all-time</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <DollarSign size={14} /> <span className="flex-1">Total Payout</span>
              <InfoBadge text="Sum of payout_amount / commission_amount on every referred lead in AED — including pending, approved, and paid. The full commission liability committed across the portfolio." />
            </div>
            <div className="text-2xl font-bold mt-1">AED {totalPayout.toLocaleString()}</div>
            <div className="text-xs text-slate-400">committed</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Wallet size={14} /> <span className="flex-1">Paid</span>
              <InfoBadge text="Subset of Total Payout that's actually been disbursed (payment_status = paid). The gap between Committed and Paid is what's still owed to partners." />
            </div>
            <div className="text-2xl font-bold mt-1 text-emerald-600">AED {paidPayout.toLocaleString()}</div>
            <div className="text-xs text-slate-400">disbursed</div>
          </CardContent></Card>
        </div>

        {/* Spec-driven KPI strip — Broker Hub lifecycle metrics. These
            are sourced from /indirect-sales/metrics so the figures
            always match what the Brokerage Hub sees (single source of
            truth: broker_sales). The strip is its own band so it can
            sit alongside the financial tiles above without competing
            for the same visual rank. */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Handshake size={14} className="text-violet-500" />
              Broker Hub lifecycle
              <span className="text-[11px] font-normal text-slate-500">
                · synchronized with Brokerage Hub
              </span>
              {/* Active drill-down chip — clears the metric filter on
                  click so the user has an obvious escape hatch from a
                  drilled state. Stays close to the title so it reads
                  as "showing X" rather than as a separate control. */}
              {metricFilter && (
                <button
                  type="button"
                  onClick={() => setMetricFilter(null)}
                  className="ml-2 inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-100"
                  title="Clear KPI drill-down"
                >
                  Drilled into: {KPI_LABELS[metricFilter]} · <X size={10} />
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiTile
                icon={Users} tone="slate"
                label="Total leads from brokers"
                value={metrics?.totals?.total_leads ?? '—'}
                active={metricFilter === 'total'}
                onClick={() => drillInto('total')}
                info="Lifetime count of every lead in the broker_sales collection — across all statuses and all brokers. Click the tile to drill the table below into this set."
              />
              <KpiTile
                icon={Eye} tone="violet"
                label="Active leads"
                value={metrics?.totals?.active_leads ?? '—'}
                sub="not yet closed"
                active={metricFilter === 'active'}
                onClick={() => drillInto('active')}
                info="Leads still in the pipeline — status is NOT won/closed_won/booked, NOT lost/cancelled/rejected. The actionable queue your team should be working."
              />
              <KpiTile
                icon={CalendarDays} tone="amber"
                label="Visits scheduled"
                value={metrics?.totals?.site_visits_scheduled ?? '—'}
                active={metricFilter === 'visits_scheduled'}
                onClick={() => drillInto('visits_scheduled')}
                info="Leads with a recorded site_visit_date — the walkthrough is on the calendar (past or future). Distinct from Visits completed, which only counts leads that reached the viewing stage."
              />
              <KpiTile
                icon={CheckCircle2} tone="emerald"
                label="Visits completed"
                value={metrics?.totals?.site_visits_completed ?? '—'}
                active={metricFilter === 'visits_completed'}
                onClick={() => drillInto('visits_completed')}
                info="Leads that actually reached the viewing stage in the funnel (or have a recorded site_visit_outcome). The hand-off point from marketing to closing."
              />
              <KpiTile
                icon={Building2} tone="indigo"
                label="Bookings generated"
                value={metrics?.totals?.bookings_generated ?? '—'}
                active={metricFilter === 'bookings'}
                onClick={() => drillInto('bookings')}
                info="Leads at booking_initiated or won status, OR with a booking_date set. Includes deals in progress + closed wins — useful for revenue forecasting."
              />
              <KpiTile
                icon={DollarSign} tone="green"
                label="Conversion rate"
                value={metrics?.totals?.conversion_rate != null
                  ? `${(metrics.totals.conversion_rate * 100).toFixed(1)}%`
                  : '—'}
                sub="leads → bookings"
                active={metricFilter === 'conversion'}
                onClick={() => drillInto('conversion')}
                info="Bookings generated ÷ Total leads from brokers × 100. Industry benchmark for partner channels is 8–15% — under 5% suggests the source mix or qualification is off."
              />
            </div>

            {/* Broker- and project-wise breakdowns. Side-by-side so
                the user can spot which brokerage is sourcing the bulk
                of bookings and which project is most enquired. */}
            {metrics && (metrics.broker_breakdown?.length > 0 || metrics.project_breakdown?.length > 0) && (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <BreakdownList
                  title="By broker"
                  icon={Handshake}
                  rows={(metrics.broker_breakdown || []).slice(0, 6)}
                  nameKey="broker_name"
                  emptyLabel="No broker activity yet"
                />
                <BreakdownList
                  title="By project"
                  icon={Building2}
                  rows={(metrics.project_breakdown || []).slice(0, 6)}
                  nameKey="project_name"
                  emptyLabel="No project enquiries yet"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline stages relocated below Agent Performance — see
            the Card just after the agent-matrix section. */}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={16} className="text-slate-400" />
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => clearDrillThen(setSearch)(e.target.value)}
                  placeholder="Search client, partner, project…"
                  className="pl-9"
                />
              </div>
              <Select value={partnerTypeFilter} onValueChange={clearDrillThen(setPartnerTypeFilter)}>
                <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Partner type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All partner types</SelectItem>
                  {partnerTypeOptions.length === 0 ? (
                    <>
                      <SelectItem value="Broker">Broker</SelectItem>
                      <SelectItem value="Referral Partner">Referral Partner</SelectItem>
                      <SelectItem value="Agency">Agency</SelectItem>
                      <SelectItem value="Channel Partner">Channel Partner</SelectItem>
                    </>
                  ) : partnerTypeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={paymentFilter} onValueChange={clearDrillThen(setPaymentFilter)}>
                <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Payment status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payments</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              {/* Internal agent assigned to the partner-referred lead. */}
              <Select value={agentFilter} onValueChange={clearDrillThen(setAgentFilter)}>
                <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  {agentOptions.unassigned > 0 && (
                    <SelectItem value="__unassigned__">
                      Unassigned ({agentOptions.unassigned})
                    </SelectItem>
                  )}
                  {agentOptions.list.map((a) => (
                    <SelectItem key={a.name} value={a.name}>
                      {a.name} ({a.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(agentFilter !== 'all' || stageFilter !== 'all' || partnerTypeFilter !== 'all' || paymentFilter !== 'all' || search) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setAgentFilter('all'); setStageFilter('all'); setPartnerTypeFilter('all');
                    setPaymentFilter('all'); setSearch(''); setMetricFilter(null);
                  }}
                >
                  Clear
                </Button>
              )}
              <span className="ml-auto text-xs text-slate-500">
                Showing {filtered.length} of {sales.length}
                {nonStageTotal !== sales.length && (
                  <span className="ml-1 text-slate-400">({nonStageTotal} after filters)</span>
                )}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Internal-Agent Performance Matrix — shows how partner-referred
            leads are distributed across the company's own agents. */}
        {agentMatrix.length >= 2 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Agent Performance</CardTitle>
              <CardDescription>
                Partner-referred leads broken out by internal agent and stage. Click an agent to filter the table; click a stage cell to drill into that bucket.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-3 sticky left-0 bg-white dark:bg-[#0a0a0f] z-10">Agent</th>
                      {PIPELINE_STAGES.map((s) => (
                        <th key={s.key} className="text-center py-2 px-2 whitespace-nowrap">{s.label}</th>
                      ))}
                      <th className="text-center py-2 px-3 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentMatrix.map((row) => {
                      const isUnassigned = row.name === '— Unassigned';
                      return (
                        <tr key={row.name}
                            className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${isUnassigned ? 'text-slate-500 italic' : ''}`}>
                          <td className="py-2 px-3 font-medium sticky left-0 bg-white dark:bg-[#0a0a0f] z-10">
                            <button
                              type="button"
                              className="text-left hover:underline decoration-dotted"
                              onClick={() => {
                                if (metricFilter) setMetricFilter(null);
                                setAgentFilter(isUnassigned ? '__unassigned__' : row.name);
                                setStageFilter('all');
                              }}
                            >
                              {isUnassigned ? 'Unassigned' : row.name}
                            </button>
                          </td>
                          {PIPELINE_STAGES.map((s) => {
                            const count = row[s.key] || 0;
                            return (
                              <td key={s.key} className="text-center py-2 px-2">
                                {count > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (metricFilter) setMetricFilter(null);
                                      setAgentFilter(isUnassigned ? '__unassigned__' : row.name);
                                      setStageFilter(s.key);
                                    }}
                                    className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded text-xs font-medium ${
                                      s.key === 'paid'
                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                        : s.key === 'closure'
                                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {count}
                                  </button>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center py-2 px-3 font-bold">{row.total}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 font-semibold">
                      <td className="py-2 px-3 sticky left-0 bg-slate-50 dark:bg-slate-800/40 z-10">Team Total</td>
                      {PIPELINE_STAGES.map((s) => {
                        const sum = agentMatrix.reduce((acc, r) => acc + (r[s.key] || 0), 0);
                        return (
                          <td key={s.key} className="text-center py-2 px-2">
                            {sum > 0 ? sum : <span className="text-slate-300 font-normal">—</span>}
                          </td>
                        );
                      })}
                      <td className="text-center py-2 px-3">
                        {agentMatrix.reduce((acc, r) => acc + r.total, 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pipeline stages — relocated to sit BELOW the Agent
            Performance matrix so the page flow reads as:
              1. Header / summary tiles
              2. Filters
              3. Agent Performance (who's working what)
              4. Pipeline (where leads are sitting)  ← + Brokerage Hub link
              5. Referred Leads table

            Pipeline stage chips drive the same `stageFilter` state, so
            clicking one filters the leads table below. The header also
            carries an "Open in Brokerage Hub →" link that opens the
            registry of brokerage partners with the active stage
            forwarded as a query param — connecting the two surfaces
            so the CEO can pivot from "which stage is loaded" to
            "which brokerages are responsible for that load". */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Pipeline</CardTitle>
              <CardDescription>
                Partner Referral → Verification → Internal Assignment → Client Contact → Site Visit → Deal Closure → Commission Approval → Partner Paid
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-violet-700 border-violet-300 hover:bg-violet-50"
              onClick={() => {
                // Pass the current stage (when one is picked) so the
                // Brokerage Hub can pre-filter its brokerage list to
                // the firms that have leads in that bucket.
                const qs = stageFilter && stageFilter !== 'all'
                  ? `?stage=${encodeURIComponent(stageFilter)}`
                  : '';
                navigate(`/leads-dashboard/brokerage-hub${qs}`);
              }}
              data-testid="pipeline-open-brokerage-hub"
              title="See which brokerages contributed leads at this stage"
            >
              <Handshake size={13} />
              Open in Brokerage Hub
              <ArrowUpRight size={13} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => clearDrillThen(setStageFilter)('all')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                  stageFilter === 'all'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                All ({nonStageTotal})
              </button>
              {PIPELINE_STAGES.map((s, idx) => (
                <React.Fragment key={s.key}>
                  <button
                    type="button"
                    onClick={() => clearDrillThen(setStageFilter)(s.key)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      stageFilter === s.key
                        ? `${s.color} text-white border-transparent ring-2 ring-offset-2 ring-slate-300`
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {s.label}
                    <span className={`ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded text-xs ${
                      stageFilter === s.key ? 'bg-white/20' : 'bg-slate-100 text-slate-700'
                    }`}>{stageCounts[s.key] || 0}</span>
                  </button>
                  {idx < PIPELINE_STAGES.length - 1 && (
                    <ChevronRight size={14} className="text-slate-300 hidden lg:inline-block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sales table */}
        <Card id="indirect-sales-leads-table">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                Referred Leads
                {/* Mirrors the chip in the KPI strip header so the
                    table makes it obvious it's currently scoped to a
                    drill-down — and gives the user a second place to
                    clear it. */}
                {metricFilter && (
                  <button
                    type="button"
                    onClick={() => setMetricFilter(null)}
                    className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-100"
                    title="Clear KPI drill-down"
                  >
                    {KPI_LABELS[metricFilter]} · {filtered.length} leads
                    <X size={10} />
                  </button>
                )}
              </CardTitle>
              <CardDescription>
                {metricFilter
                  ? `Showing leads behind "${KPI_LABELS[metricFilter]}". Click the chip or tile again to clear.`
                  : 'Click the eye icon for the full partner + commission breakdown.'}
              </CardDescription>
            </div>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setColumnsOpen((v) => !v)}
              >
                <Columns3 size={14} className="mr-1.5" /> Columns
              </Button>
              {columnsOpen && (
                <div
                  className="absolute right-0 mt-1 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-20 p-1"
                  // Click-outside dismissal would need a click-away
                  // listener; for now Done button + clicking another
                  // column toggle keeps it usable. Esc to close would
                  // be a nice follow-up.
                >
                  <div className="text-[11px] uppercase text-slate-500 px-2 pt-1 pb-1">
                    Show columns
                  </div>
                  {COLUMN_DEFS.map((c) => {
                    const checked = showCol(c.key);
                    const isDisabled = c.required;
                    return (
                      <button
                        type="button"
                        key={c.key}
                        disabled={isDisabled}
                        onClick={() => setVisibleCols((v) => ({ ...v, [c.key]: !v[c.key] }))}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center justify-between ${
                          isDisabled
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span>{c.label}{isDisabled && <span className="text-[10px] text-slate-400 ml-1">(always on)</span>}</span>
                        {checked && <Check size={14} className="text-violet-600" />}
                      </button>
                    );
                  })}
                  <div className="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1 flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => setColumnsOpen(false)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-slate-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                No partner-referred leads match these filters.
              </div>
            ) : (
              <ScrollArea className="h-[520px]">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr>
                      {showCol('client')   && <th className="text-left py-2 px-3">Client</th>}
                      {showCol('partner')  && <th className="text-left py-2 px-3">Partner</th>}
                      {showCol('project')  && <th className="text-left py-2 px-3">Project</th>}
                      {showCol('stage')    && <th className="text-left py-2 px-3">Stage</th>}
                      {showCol('payout')   && <th className="text-right py-2 px-3">Payout</th>}
                      {showCol('payment')  && <th className="text-center py-2 px-3">Payment</th>}
                      {showCol('budget')   && <th className="text-right py-2 px-3">Client budget</th>}
                      {showCol('overview') && <th className="text-left py-2 px-3">Notes / overview</th>}
                      {showCol('view')     && <th className="text-center py-2 px-3 w-12">View</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => {
                      const stage = PIPELINE_STAGES.find((s) => s.key === stageFor(row));
                      const partner = partnerById[row.broker_id];
                      const pay = (row.payment_status || '').toLowerCase();
                      // Project cell prefers the multi-select names
                      // when present; falls back to the legacy single
                      // string. We render the first two as inline
                      // badges plus a "+N" overflow chip — keeps the
                      // column from blowing out on a 5-project lead.
                      const projectNames = (
                        Array.isArray(row.project_enquired_names) && row.project_enquired_names.length
                          ? row.project_enquired_names
                          : (row.project_name ? [row.project_name] : [])
                      );
                      return (
                        <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          {showCol('client') && (
                            <td className="py-2 px-3 font-medium">
                              <div>{fieldValue(row.client_name)}</div>
                              {/* Linkage chips — make the Brokerage-Hub /
                                  Indirect-Sales sync visible at a glance.
                                  Both are tiny pills so the column stays
                                  narrow. */}
                              {(row.site_visit_date || (Array.isArray(row.documents) && row.documents.length > 0)) && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {row.site_visit_date && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-amber-50 border-amber-200 text-amber-700 font-normal"
                                      title={`Site visit · ${new Date(row.site_visit_date).toLocaleString()}`}
                                    >
                                      <CalendarDays size={9} className="mr-0.5" />
                                      Visit
                                    </Badge>
                                  )}
                                  {Array.isArray(row.documents) && row.documents.length > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-violet-50 border-violet-200 text-violet-700 font-normal"
                                      title="UAE documents on file"
                                    >
                                      <FileText size={9} className="mr-0.5" />
                                      {row.documents.length} doc{row.documents.length === 1 ? '' : 's'}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                          {showCol('partner') && (
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300">
                              <div className="font-medium">{partner?.company_name || partner?.name || row.broker_name || '—'}</div>
                              <div className="text-xs text-slate-400">{partner?.partner_type || partner?.type || ''}</div>
                            </td>
                          )}
                          {showCol('project') && (
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300">
                              {projectNames.length === 0 ? '—' : (
                                <div className="flex flex-wrap gap-1">
                                  {projectNames.slice(0, 2).map((n) => (
                                    <Badge key={n} variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-800">
                                      {n}
                                    </Badge>
                                  ))}
                                  {projectNames.length > 2 && (
                                    <Badge variant="outline" className="text-[10px]">
                                      +{projectNames.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                          {showCol('stage') && (
                            <td className="py-2 px-3">
                              {stage && (
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs text-white ${stage.color}`}>
                                  {stage.label}
                                </span>
                              )}
                            </td>
                          )}
                          {showCol('payout') && (
                            <td className="py-2 px-3 text-right font-mono">
                              {row.payout_amount || row.commission_amount
                                ? `AED ${Number(row.payout_amount || row.commission_amount).toLocaleString()}`
                                : '—'}
                            </td>
                          )}
                          {showCol('payment') && (
                            <td className="py-2 px-3 text-center">
                              {pay ? (
                                <Badge className={`text-xs border ${PAYMENT_BADGE[pay] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                                  {pay}
                                </Badge>
                              ) : '—'}
                            </td>
                          )}
                          {showCol('budget') && (
                            <td className="py-2 px-3 text-right font-mono text-xs">
                              {formatBudget(row.client_budget_min, row.client_budget_max, row.client_budget_currency)}
                            </td>
                          )}
                          {showCol('overview') && (
                            <td className="py-2 px-3 text-xs text-slate-600 dark:text-slate-300 max-w-[260px]">
                              <span className="line-clamp-2" title={row.lead_overview || ''}>
                                {row.lead_overview || '—'}
                              </span>
                            </td>
                          )}
                          {showCol('view') && (
                            <td className="py-2 px-3 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => { setSelected(row); setDetailOpen(true); }}
                                title="View partner + commission detail"
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <SaleDetailDialog
          row={selected}
          partner={selected ? partnerById[selected.broker_id] : null}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
      </div>
    </div>
  );
}

// ============ KPI strip helpers ============
//
// Small enough to live inline. Same visual language as the rest of the
// page — light tinted backgrounds, icon-pill on the left, big number,
// small sub-label.

const TONE_CLASSES = {
  slate:   { bg: 'bg-slate-50',   text: 'text-slate-700',   ring: 'ring-slate-200' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  ring: 'ring-violet-200' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-200' },
  green:   { bg: 'bg-green-50',   text: 'text-green-700',   ring: 'ring-green-200' },
};

const KpiTile = ({ icon: Icon, tone = 'slate', label, value, sub, active = false, onClick, info }) => {
  const t = TONE_CLASSES[tone] || TONE_CLASSES.slate;
  // When the tile is interactive we render a <button> so keyboard users
  // get focus + Enter for free. Active state lifts the ring colour
  // and adds a shadow so the user can see at a glance which metric is
  // currently drilled into.
  const interactive = typeof onClick === 'function';
  const base = `rounded-lg border bg-white p-3 ring-1 text-left transition ${
    active
      ? `border-violet-400 ring-violet-300 shadow-md ${t.bg}`
      : `border-slate-200 ${t.ring}`
  } ${interactive ? 'cursor-pointer hover:shadow-sm hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-400' : ''}`;
  const inner = (
    <>
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${t.bg} ${t.text}`}>
          <Icon size={13} />
        </span>
        <span className="flex-1 text-[11px] font-medium text-slate-500">{label}</span>
        {active ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-600">
            drilled
          </span>
        ) : (
          // We stop click propagation on the info badge so clicking
          // the (i) doesn't accidentally trigger the tile's drill-down.
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <InfoBadge text={info} />
          </span>
        )}
      </div>
      <div className="mt-1.5 text-xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      {interactive && !active && (
        <div className="mt-1 text-[10px] text-violet-500 opacity-0 group-hover:opacity-100">
          click to drill ↓
        </div>
      )}
    </>
  );
  if (!interactive) {
    return <div className={base}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} group w-full`}
      aria-pressed={active}
      title={active ? 'Click again to clear drill-down' : `Drill into ${label}`}
    >
      {inner}
    </button>
  );
};

const BreakdownList = ({ title, icon: Icon, rows, nameKey, emptyLabel }) => {
  // Max leads across rows — drives the inline bar width so the visual
  // ranking is obvious without an extra chart library.
  const maxLeads = Math.max(1, ...rows.map((r) => r.leads || 0));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
        <Icon size={13} /> {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">{emptyLabel}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => {
            const pct = Math.round(((r.leads || 0) / maxLeads) * 100);
            return (
              <li key={`${r[nameKey] || 'row'}-${i}`} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-slate-700" title={r[nameKey]}>
                    {r[nameKey] || '—'}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-500">
                    {r.leads} leads · {r.bookings || 0} booked
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-violet-400"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
